import React, { useEffect, Suspense, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatPanelProvider } from './contexts/ChatPanelContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { GoogleMapsProvider } from './contexts/GoogleMapsProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import SuperAdminRoute from './components/auth/SuperAdminRoute';
import AgentRoute from './components/auth/AgentRoute';
import HomeownerRoute from './components/auth/HomeownerRoute';
import NewPropertyRoute from './components/auth/NewPropertyRoute';
import AgentReadyGate from './components/agent/onboarding/AgentReadyGate';
import CookieConsentBanner from './components/CookieConsentBanner';
import { DockedChatPanel } from './components/MultiModalLayout';
import { initializePreloading, preloadBasedOnRoute } from './utils/preloader';
import { initGA, setAnalyticsConsent, trackPageView } from './utils/analytics';
import { applyRouteSeo, hasServerRenderedHead, isIndexablePath } from './utils/seo';
import { canUseAnalyticsCookies, COOKIE_CONSENT_EVENT } from './utils/cookieConsent';
import { isAdmin, isAgent, isLawyer, getPendingProfessionalRole } from './constants/roles';

// ============================================================================
// EAGER LOADED COMPONENTS (Critical for initial page load)
// ============================================================================
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import { tr } from 'date-fns/locale';

// ============================================================================
// LAZY LOADED COMPONENTS (Code Splitting)
// ============================================================================
// Heavy dashboard and core app pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AgentDashboard = React.lazy(() => import('./components/agent/AgentDashboard'));
const AgentSettingsPage = React.lazy(() => import('./pages/agent/AgentSettingsPage'));
const AgentOnboardingPage = React.lazy(() => import('./pages/agent/OnboardingPage'));
const BrokeragePage = React.lazy(() => import('./pages/agent/BrokeragePage'));
const AgentEmailPage = React.lazy(() => import('./pages/agent/AgentEmailPage'));
const AgentCalendarPage = React.lazy(() => import('./pages/agent/AgentCalendarPage'));
const AgentContactsPage = React.lazy(() => import('./pages/agent/AgentContactsPage'));
const AgentPropertiesPage = React.lazy(() => import('./pages/agent/AgentPropertiesPage'));
const AgentPropertyWorkspace = React.lazy(() => import('./pages/agent/AgentPropertyWorkspace'));
const AgentApprovalsPage = React.lazy(() => import('./pages/agent/AgentApprovalsPage'));
const AgentTasksPage = React.lazy(() => import('./pages/agent/AgentTasksPage'));
const AgentInquiriesPage = React.lazy(() => import('./pages/agent/AgentInquiriesPage'));
const InviteLandingPage = React.lazy(() => import('./pages/InviteLandingPage'));
const LawyerDashboard = React.lazy(() => import('./components/lawyer/LawyerDashboard'));
const PendingProfessionalDashboard = React.lazy(() => import('./components/PendingProfessionalDashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));

// Property-related pages (heaviest components due to maps/data)
const FindPropertiesPage = React.lazy(() => import('./pages/FindPropertiesPage'));
const PropertyDetail = React.lazy(() => import('./pages/PropertyDetail'));
const MyPropertyPage = React.lazy(() => import('./pages/MyPropertyPage'));
const MyPropertyDashboardPage = React.lazy(() => import('./pages/MyPropertyDashboardPage'));
const AddPropertyPage = React.lazy(() => import('./pages/AddPropertyPage'));
const MyPropertiesPage = React.lazy(() => import('./pages/MyPropertiesPage'));
const PropertySellingDashboard = React.lazy(() => import('./components/property/SellingJourney/PropertySellingDashboard'));
const SimilarProperties = React.lazy(() => import('./pages/SimilarProperties'));
const MarketingStudio = React.lazy(() => import('./pages/MarketingStudio'));
const MediaStudio = React.lazy(() => import('./pages/MediaStudio'));
const PublicPropertyMarketingPage = React.lazy(() => import('./pages/PublicPropertyMarketingPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Chat and communication features
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const SchedulePage = React.lazy(() => import('./pages/SchedulePage'));

// Admin and utility pages
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const RBACManagement = React.lazy(() => import('./pages/RBACManagement'));
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsersPage'));
const AdminUserPropertiesPage = React.lazy(() => import('./pages/AdminUserPropertiesPage'));
const AdminConsultationsPage = React.lazy(() => import('./pages/AdminConsultationsPage'));
const AdminConversationFeedbackPage = React.lazy(() => import('./pages/AdminConversationFeedbackPage'));
const AdminMarketingTargetsPage = React.lazy(() => import('./pages/AdminMarketingTargetsPage'));
const AdminOutreachCampaignsPage = React.lazy(() => import('./pages/AdminOutreachCampaignsPage'));
const AdminPlatformMarketingQueuePage = React.lazy(() => import('./pages/AdminPlatformMarketingQueuePage'));
const AdminBlogPage = React.lazy(() => import('./pages/AdminBlogPage'));
const AdminBrokeragesPage = React.lazy(() => import('./pages/AdminBrokeragesPage'));
const AllHistoryPage = React.lazy(() => import('./pages/AllHistoryPage'));
const PromptsPage = React.lazy(() => import('./pages/PromptsPage'));
const HistoryPage = React.lazy(() => import('./pages/HistoryPage'));
const MetricsPage = React.lazy(() => import('./pages/MetricsPage'));
const LogsPage = React.lazy(() => import('./pages/LogsPage'));
const AdminCMA = React.lazy(() => import('./pages/AdminCMA'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const ActivityPage = React.lazy(() => import('./pages/ActivityPage'));

// Static/info pages
const About = React.lazy(() => import('./pages/About'));
const MarketPage = React.lazy(() => import('./pages/MarketPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const ServiceUnavailablePage = React.lazy(() => import('./pages/ServiceUnavailablePage'));
const OutreachLandingPage = React.lazy(() => import('./pages/OutreachLandingPage'));
const SellSmarterPage = React.lazy(() => import('./pages/SellSmarterPage'));

// ============================================================================
// LOADING COMPONENT
// ============================================================================
const PageLoadingSpinner = ({ message = "Loading..." }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      gap: 2,
    }}
  >
    <CircularProgress 
      size={40} 
      thickness={4}
      sx={{ 
        color: theme => theme.palette.primary.main,
      }}
    />
    <Typography 
      variant="body1" 
      color="text.secondary"
      sx={{ fontWeight: 500 }}
    >
      {message}
    </Typography>
  </Box>
);

// ============================================================================
// SUSPENSE WRAPPER COMPONENTS
// ============================================================================
const SuspenseWrapper = ({ children, fallback }) => (
  <Suspense fallback={fallback || <PageLoadingSpinner />}>
    {children}
  </Suspense>
);

const ProtectedSuspenseRoute = ({ children, fallback }) => (
  <ProtectedRoute>
    <SuspenseWrapper fallback={fallback}>
      {children}
    </SuspenseWrapper>
  </ProtectedRoute>
);

// Component for preloading logic that needs both AuthContext and Router context
function PreloadingManager() {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Initialize preloading strategies
  useEffect(() => {
    initializePreloading(location.pathname, !!currentUser);
  }, [location.pathname, currentUser]);

  // Preload based on route changes
  useEffect(() => {
    preloadBasedOnRoute(location.pathname);
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

// Google Analytics route tracker — sends page_view on every navigation
function RouteTracker() {
  const location = useLocation();
  const [canTrackAnalytics, setCanTrackAnalytics] = useState(canUseAnalyticsCookies());

  useEffect(() => {
    const updateConsentState = () => {
      setCanTrackAnalytics(canUseAnalyticsCookies());
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, updateConsentState);
    window.addEventListener('storage', updateConsentState);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, updateConsentState);
      window.removeEventListener('storage', updateConsentState);
    };
  }, []);

  // Initialize GA4 once on mount
  useEffect(() => {
    initGA();
  }, []);

  // Keep GA consent mode in sync with cookie preferences
  useEffect(() => {
    // setAnalyticsConsent(canTrackAnalytics);
    setAnalyticsConsent(true);
  }, [canTrackAnalytics]);

  // Head tags per route. The first route keeps the head the server rendered
  // (see hasServerRenderedHead); every later navigation gets the client head.
  const firstSeoRunRef = useRef(true);
  useEffect(() => {
    const keepServerHead = firstSeoRunRef.current && hasServerRenderedHead(location.pathname);
    firstSeoRunRef.current = false;
    if (!keepServerHead) {
      applyRouteSeo(location.pathname);
    }
  }, [location.pathname]);

  // Track page view on every route change
  useEffect(() => {
    // if (canTrackAnalytics) {
    trackPageView(location.pathname + location.search);
    // }
  }, [location, canTrackAnalytics]);

  return null;
}

function ScrollToTopOnNavigate() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocationKeyRef = useRef(location.key);
  const savedScrollPositionsRef = useRef(new Map());

  useEffect(() => {
    const previousKey = previousLocationKeyRef.current;
    if (previousKey && previousKey !== location.key) {
      savedScrollPositionsRef.current.set(previousKey, {
        left: window.scrollX,
        top: window.scrollY,
      });

      // Keep memory bounded for long sessions.
      if (savedScrollPositionsRef.current.size > 300) {
        const oldestKey = savedScrollPositionsRef.current.keys().next().value;
        savedScrollPositionsRef.current.delete(oldestKey);
      }
    }

    // A page that only records what is open on it (a panel, ?open=) stays where it is.
    if (location.state?.keepScroll) {
      previousLocationKeyRef.current = location.key;
      return;
    }

    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        previousLocationKeyRef.current = location.key;
        return;
      }
    }

    if (navigationType === 'POP') {
      const savedPosition = savedScrollPositionsRef.current.get(location.key);
      if (savedPosition) {
        window.scrollTo({
          left: savedPosition.left,
          top: savedPosition.top,
          behavior: 'auto',
        });
      }

      previousLocationKeyRef.current = location.key;
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    previousLocationKeyRef.current = location.key;
  }, [location, navigationType]);

  return null;
}

function UnifiedDashboardEntry() {
  const { currentUser } = useAuth();

  if (isAdmin(currentUser)) {
    return <AdminDashboard />;
  }

  // Agents, and people who signed up as one, pass the onboarding flow first
  // (Agent ID, then the optional steps); the gate sends them to /agent/onboarding
  // until it is finished and otherwise renders the dashboard in place, so
  // ?listing= and any other query stay on the URL.
  if (isAgent(currentUser) || getPendingProfessionalRole(currentUser) === 'agent') {
    return (
      <AgentReadyGate>
        <AgentDashboard />
      </AgentReadyGate>
    );
  }

  if (isLawyer(currentUser)) {
    return (
      <LawyerDashboard />
    );
  }

  // Registered as a lawyer but not yet approved by KeyTa: show that workspace
  // in its pending state instead of the client dashboard.
  const pendingRole = getPendingProfessionalRole(currentUser);
  if (pendingRole) {
    return (
      <PendingProfessionalDashboard role={pendingRole} />
    );
  }

  return (
    <Dashboard />
  );
}

/**
 * `/my-property-mls/:id` was the listing form's own URL before the property
 * views were collapsed into one family. Anything still pointing there lands on
 * the equivalent tab instead of a dead end.
 */
/**
 * `/properties-map` was a second, orphaned copy of the search with its own
 * filters and cards. The search page now shows the list and the map together
 * above `md`, so this sends people to the one search that exists.
 */
function LegacyPropertiesMapRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', 'map');

  return <Navigate to={`/properties?${params.toString()}`} replace />;
}

function LegacyPropertyFormRedirect() {
  const { propertyId } = useParams();
  const location = useLocation();

  return (
    <Navigate
      to={`/my-property/${propertyId}/details${location.search}${location.hash}`}
      replace
    />
  );
}

function LegacyDashboardRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={`/dashboard${location.search}${location.hash}`}
      replace
    />
  );
}

// Separate component for the app content that needs AuthContext
function AppContent() {
  const { logout } = useAuth();
  
  useEffect(() => {
    const handleAuthExpired = async () => {
      await logout();
      window.location.href = '/login';
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, [logout]);

  useEffect(() => {
    const handleServiceUnavailable = () => {
      // A public page stays put during an outage: crawlers that render it would
      // otherwise follow a JavaScript redirect to a noindexed URL. Its own
      // error states cover the failed requests.
      if (isIndexablePath(window.location.pathname)) {
        return;
      }
      if (window.location.pathname !== '/service-unavailable') {
        window.location.href = '/service-unavailable';
      }
    };

    window.addEventListener('service:unavailable', handleServiceUnavailable);
    return () => window.removeEventListener('service:unavailable', handleServiceUnavailable);
  }, []);

  return (
    <Router>
      <ChatPanelProvider>
        <PreloadingManager />
        <RouteTracker />
        <ScrollToTopOnNavigate />
            <Routes>
              {/* ========================================== */}
              {/* EAGER LOADED ROUTES (Critical paths) */}
              {/* ========================================== */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* Public outreach landing (QR / mail attribution) */}
              <Route
                path="/outreach/:code"
                element={
                  <OutreachLandingPage />
                }
              />
              {/* Generic mailer landing: shared URL, visitor claims their address inline */}
              <Route
                path="/sell-smarter/:code?"
                element={
                  <SellSmarterPage />
                }
              />

              <Route 
                path="/signup" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading signup..." />}>
                    <Signup />
                  </SuspenseWrapper>
                } 
              />

              {/* ========================================== */}
              {/* LAZY LOADED ROUTES (Code Split) */}
              {/* ========================================== */}
              
              {/* Static/Info Pages */}
              <Route 
                path="/about" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading About..." />}>
                    <About />
                  </SuspenseWrapper>
                } 
              />
              <Route 
                path="/market" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading market insights..." />}>
                    <MarketPage />
                  </SuspenseWrapper>
                } 
              />
              <Route 
                path="/faq" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading FAQ..." />}>
                    <FAQPage />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/blog"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Blog..." />}>
                    <BlogListPage />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Post..." />}>
                    <BlogPostPage />
                  </SuspenseWrapper>
                }
              />
              <Route 
                path="/contact" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Contact..." />}>
                    <ContactPage />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/privacy-policy"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Privacy Policy..." />}>
                    <PrivacyPolicyPage />
                  </SuspenseWrapper>
                }
              />
              <Route
                path="/terms-of-service"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Terms of Service..." />}>
                    <TermsOfServicePage />
                  </SuspenseWrapper>
                }
              />
              <Route 
                path="/auth/callback" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Processing authentication..." />}>
                    <OAuthCallback />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/service-unavailable"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Preparing status page..." />}>
                    <ServiceUnavailablePage />
                  </SuspenseWrapper>
                }
              />
              {/* Brokerage invitation landing: public, so the visitor sees who invited them first */}
              <Route
                path="/invite/:token"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading invitation..." />}>
                    <InviteLandingPage />
                  </SuspenseWrapper>
                }
              />

              {/* Core App Pages */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Dashboard..." />}
                  >
                    <UnifiedDashboardEntry />
                  </ProtectedSuspenseRoute>
                }
              />
              {/* Everything an agent sets, in one place (pages/agent/AgentSettingsPage); pending
                  agents too, so they can connect and set up while their Agent ID is confirmed. */}
              <Route
                path="/agent-settings"
                element={
                  <AgentRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading settings..." />}>
                      <AgentSettingsPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              <Route
                path="/agent/brokerage"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading your brokerage..." />}
                  >
                    <BrokeragePage />
                  </ProtectedSuspenseRoute>
                }
              />
              <Route
                path="/agent/onboarding"
                element={
                  <AgentRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading setup..." />}>
                      <AgentOnboardingPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              {/* The agent's tools, opened from the cards on the agent dashboard */}
              <Route
                path="/agent/email"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading email..." />}>
                      <AgentEmailPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              <Route
                path="/agent/calendar"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading calendar..." />}>
                      <AgentCalendarPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              <Route
                path="/agent/contacts"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading contacts..." />}>
                      <AgentContactsPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              {/* Social media moved into Settings. */}
              <Route path="/agent/social" element={<Navigate to="/agent-settings?section=social" replace />} />
              {/* The agent's own listings, and one listing's workspace: a shared header with a
                  routed page per view. Photos, marketing, CMA and details are the seller's pages,
                  shown inside the workspace (components/agent/workspace/AgentWorkspaceContext.js). */}
              <Route
                path="/agent/properties"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading your listings..." />}>
                      <AgentPropertiesPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              {/* Adding a home to list: the step-by-step page (pages/AddPropertyPage). */}
              <Route
                path="/agent/properties/new"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading..." />}>
                      <AddPropertyPage audience="agent" />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              <Route
                path="/agent/properties/:propertyId"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading your listing..." />}>
                      <AgentPropertyWorkspace />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              >
                {/* The property page: the seller's /my-property/:id page, for the agent. */}
                <Route index element={<MyPropertyDashboardPage />} />
                <Route path="/agent/properties/:propertyId/photos" element={<MediaStudio />} />
                <Route path="/agent/properties/:propertyId/marketing" element={<MarketingStudio />} />
                <Route path="/agent/properties/:propertyId/cma" element={<SimilarProperties />} />
                <Route path="/agent/properties/:propertyId/details" element={<MyPropertyPage />} />
              </Route>
              {/* What KeyTa made for the agent's listings, waiting for their approval. */}
              <Route
                path="/agent/approvals"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading your approvals..." />}>
                      <AgentApprovalsPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              {/* The agent's tasks, and the inquiries KeyTa sorted from their messages. */}
              <Route
                path="/agent/tasks"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading your tasks..." />}>
                      <AgentTasksPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              <Route
                path="/agent/inquiries"
                element={
                  <AgentRoute requireReady>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading your inquiries..." />}>
                      <AgentInquiriesPage />
                    </SuspenseWrapper>
                  </AgentRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Profile..." />}
                  >
                    <Profile />
                  </ProtectedSuspenseRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading Notifications..." />}
                  >
                    <NotificationsPage />
                  </ProtectedSuspenseRoute>
                }
              />
              <Route
                path="/activity"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading Activity..." />}
                  >
                    <ActivityPage />
                  </ProtectedSuspenseRoute>
                }
              />

              {/* Chat Features */}
              <Route 
                path="/chat" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Chat..." />}>
                    <ChatPage />
                  </SuspenseWrapper>
                } 
              />

              {/* Property Pages (Heaviest components) */}
              <Route 
                path="/properties" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Properties..." />}>
                    <FindPropertiesPage />
                  </SuspenseWrapper>
                } 
              />
              {/* MLS listings by city; server-rendered hub, see routers/render.py */}
              <Route 
                path="/properties/:citySlug" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Properties..." />}>
                    <FindPropertiesPage />
                  </SuspenseWrapper>
                } 
              />
              <Route 
                path="/properties-map"
                element={<LegacyPropertiesMapRedirect />}
              />
              <Route 
                path="/property/:listingKey" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Property Details..." />}>
                    <PropertyDetail />
                  </SuspenseWrapper>
                } 
              />
              <Route
                path="/:provinceSlug/:citySlug/:addressSlug/home/:publicCode"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Public Listing..." />}>
                    <PublicPropertyMarketingPage />
                  </SuspenseWrapper>
                }
              />
              <Route 
                path="/schedule" 
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Schedule..." />}>
                    <SchedulePage />
                  </SuspenseWrapper>
                } 
              />

              {/* User Property Management */}
              {/* Adding a home: the step-by-step page; an agent is sent to /agent/properties/new. */}
              <Route
                path="/my-property/new"
                element={
                  <ProtectedSuspenseRoute fallback={<PageLoadingSpinner message="Loading..." />}>
                    <NewPropertyRoute>
                      <AddPropertyPage />
                    </NewPropertyRoute>
                  </ProtectedSuspenseRoute>
                }
              />
              {/* The old add-property form's URL. */}
              <Route path="/my-property/new/details" element={<Navigate to="/my-property/new" replace />} />
              <Route 
                path="/my-property/:propertyId" 
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Property Dashboard..." />}
                  >
                    <HomeownerRoute>
                      <MyPropertyDashboardPage />
                    </HomeownerRoute>
                  </ProtectedSuspenseRoute>
                } 
              />
              {/* The listing form is a tab of the property, not a separate page. */}
              <Route
                path="/my-property/:propertyId/details"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading Listing Details..." />}
                  >
                    <HomeownerRoute page="details">
                      <MyPropertyPage />
                    </HomeownerRoute>
                  </ProtectedSuspenseRoute>
                }
              />
              {/* Old form URL; keep it working and send it to the canonical one. */}
              <Route
                path="/my-property-mls/:propertyId"
                element={<LegacyPropertyFormRedirect />}
              />
              <Route 
                path="/my-property/:propertyId/journey" 
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Selling Journey..." />}
                  >
                    <HomeownerRoute page="journey">
                      <PropertySellingDashboard />
                    </HomeownerRoute>
                  </ProtectedSuspenseRoute>
                } 
              />
              <Route 
                path="/my-property/:propertyId/similar" 
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Similar Properties..." />}
                  >
                    <HomeownerRoute page="similar">
                      <SimilarProperties />
                    </HomeownerRoute>
                  </ProtectedSuspenseRoute>
                } 
              />
              <Route 
                path="/my-property/:propertyId/marketing" 
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading Marketing Studio..." />}
                  >
                    <HomeownerRoute page="marketing">
                      <MarketingStudio />
                    </HomeownerRoute>
                  </ProtectedSuspenseRoute>
                } 
              />
              <Route
                path="/my-property/:propertyId/media"
                element={
                  <ProtectedSuspenseRoute
                    fallback={<PageLoadingSpinner message="Loading your photos..." />}
                  >
                    <HomeownerRoute page="media">
                      <MediaStudio />
                    </HomeownerRoute>
                  </ProtectedSuspenseRoute>
                }
              />
              <Route 
                path="/my-properties" 
                element={
                  <ProtectedSuspenseRoute 
                    fallback={<PageLoadingSpinner message="Loading My Properties..." />}
                  >
                    <MyPropertiesPage />
                  </ProtectedSuspenseRoute>
                } 
              />

              {/* Admin Pages */}
              <Route 
                path="/admin" 
                element={
                  <AdminRoute>
                    <LegacyDashboardRedirect />
                  </AdminRoute>
                } 
              />

              {/* Settings Page - Super Admin Only */}
              <Route 
                path="/admin/settings" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Settings..." />}>
                      <SettingsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              
              {/* Admin CMA Explorer */}
              <Route 
                path="/admin/cma" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Admin CMA..." />}>
                      <AdminCMA />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />

              {/* RBAC Management Page - Super Admin Only */}
              <Route 
                path="/admin/rbac-management" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading RBAC Management..." />}>
                      <RBACManagement />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />

              <Route
                path="/admin/users-management"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Users And Visitors..." />}>
                      <AdminUsersPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/brokerages"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Brokerages..." />}>
                      <AdminBrokeragesPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/users-properties"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading User Properties..." />}>
                      <AdminUserPropertiesPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/consultations"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Booked Consultations..." />}>
                      <AdminConsultationsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/conversation-feedback"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Conversation Feedback..." />}>
                      <AdminConversationFeedbackPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/marketing-targets"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Marketing Targets..." />}>
                      <AdminMarketingTargetsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/outreach-campaigns"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Outreach Campaigns..." />}>
                      <AdminOutreachCampaignsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/platform-marketing"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Platform Marketing Queue..." />}>
                      <AdminPlatformMarketingQueuePage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route
                path="/admin/blog"
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Blog Manager..." />}>
                      <AdminBlogPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                }
              />

              <Route 
                path="/admin/history" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading History..." />}>
                      <AllHistoryPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/history/:threadId" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Thread History..." />}>
                      <HistoryPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/prompts" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading Prompts..." />}>
                      <PromptsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/metrics" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading System Metrics..." />}>
                      <MetricsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/logs" 
                element={
                  <SuperAdminRoute>
                    <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading System Logs..." />}>
                      <LogsPage />
                    </SuspenseWrapper>
                  </SuperAdminRoute>
                } 
              />

              <Route
                path="*"
                element={
                  <SuspenseWrapper fallback={<PageLoadingSpinner message="Loading page..." />}>
                    <NotFoundPage />
                  </SuspenseWrapper>
                }
              />
            </Routes>
            {/* One chat panel for every page that docks it, so a page change never rebuilds it. */}
            <DockedChatPanel />
            <CookieConsentBanner />
      </ChatPanelProvider>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <GoogleMapsProvider>
            <AppContent />
          </GoogleMapsProvider>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;

